import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, StickyNote, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';

export function NotesPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [newNote, setNewNote] = useState('');

  const { data: notes, isLoading, refetch } = useQuery({
    queryKey: ['personal-notes'],
    queryFn: async () => {
      const { data } = await supabase
        .from('personal_note')
        .select('id, content, created_at, updated_at')
        .order('updated_at', { ascending: false });
      return data ?? [];
    },
  });

  const handleCreate = async () => {
    if (!newNote.trim()) return;
    await supabase.from('personal_note').insert({ content: newNote } as never);
    setNewNote('');
    setShowCreate(false);
    refetch();
  };

  const handleDelete = async (id: string) => {
    await supabase.from('personal_note').delete().eq('id', id);
    refetch();
  };

  return (
    <div>
      <PageHeader
        title="Notes"
        action={
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Note
          </Button>
        }
      />

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      )}

      {!isLoading && notes?.length === 0 && (
        <EmptyState
          icon={StickyNote}
          title="No notes yet"
          description="Create a quick note to keep track of your thoughts."
          actionLabel="Create Note"
          onAction={() => setShowCreate(true)}
        />
      )}

      {notes && notes.length > 0 && (
        <div className="space-y-3">
          {notes.map((note) => (
            <Card key={note.id}>
              <CardContent className="flex items-start gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <p className="whitespace-pre-wrap text-sm">{note.content}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {new Date(note.updated_at).toLocaleString()}
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(note.id)}>
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Note</DialogTitle></DialogHeader>
          <Textarea rows={5} placeholder="Write your note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} />
          <div className="flex justify-end">
            <Button onClick={handleCreate} disabled={!newNote.trim()}>Save Note</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
